import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { FiEdit2 } from 'react-icons/fi';
import { apiFetch, parseJsonSafe } from '../../utils/api';
import { getLocalDateTimeValue, toLocalDateTimeString } from '../../utils/dateUtils';
import { formatCurrency } from '../../utils/invoiceUtils';
import ProjectCreateModal from '../../components/projects/ProjectCreateModal';

const stageOptions = [
  { value: 'nuevo', label: 'Nuevo', color: 'secondary' },
  { value: 'contactado', label: 'Contactado', color: 'info' },
  { value: 'reunion', label: 'Reunión', color: 'primary' },
  { value: 'presupuestado', label: 'Presupuestado', color: 'warning' },
  { value: 'ganado', label: 'Ganado', color: 'success' },
  { value: 'perdido', label: 'Perdido', color: 'danger' }
];
const closedStages = new Set(['ganado', 'perdido']);

const createCommForm = () => ({
  canal: 'call',
  direccion: 'out',
  asunto: '',
  cuerpo_texto: '',
  fecha: getLocalDateTimeValue()
});

const normalizeStage = (value) => {
  const raw = String(value || '').trim().toLowerCase();
  return stageOptions.some((s) => s.value === raw) ? raw : 'nuevo';
};

const ClientsLeadsPage = () => {
  const navigate = useNavigate();
  const token = localStorage.getItem('token');
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [search, setSearch] = useState('');
  const [stageFilter, setStageFilter] = useState('');
  const [showClosed, setShowClosed] = useState(false);
  const [showStaleOnly, setShowStaleOnly] = useState(false);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [stageSettings, setStageSettings] = useState({});
  const [settingsDraft, setSettingsDraft] = useState({});
  const [savingSettings, setSavingSettings] = useState(false);
  const [commsModal, setCommsModal] = useState({ open: false, lead: null });
  const [projectModal, setProjectModal] = useState({ open: false, clientId: null, clientName: '' });
  const [commsLoading, setCommsLoading] = useState(false);
  const [commsSaving, setCommsSaving] = useState(false);
  const [commsError, setCommsError] = useState('');
  const [commsList, setCommsList] = useState([]);
  const [commsExpanded, setCommsExpanded] = useState({});
  const [commsEditing, setCommsEditing] = useState(null);
  const [commsForm, setCommsForm] = useState(createCommForm);
  const commsContentRef = useRef(null);

  const visibleStages = useMemo(
    () => (showClosed ? stageOptions : stageOptions.filter((s) => !closedStages.has(s.value))),
    [showClosed]
  );

  const handleUnauthorized = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('role');
    localStorage.removeItem('userEmail');
    navigate('/login');
  };

  const fetchLeads = async () => {
    setLoading(true);
    setError('');
    try {
      const params = new URLSearchParams();
      if (search) params.append('search', search);
      if (stageFilter) params.append('stage', stageFilter);
      const res = await apiFetch(`/api/clients/leads-board?${params.toString()}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const data = await parseJsonSafe(res);
      if (res.status === 401) return handleUnauthorized();
      if (!res.ok) throw new Error(data.message || 'Error cargando leads');
      setLeads(Array.isArray(data.data) ? data.data : []);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const loadStageSettings = async () => {
    try {
      const res = await apiFetch('/api/clients/lead-stages/settings', {
        headers: { Authorization: `Bearer ${token}` }
      });
      const data = await parseJsonSafe(res);
      if (res.status === 401) return handleUnauthorized();
      if (!res.ok) throw new Error(data.message || 'Error cargando configuración');
      setStageSettings(data.settings || {});
      setSettingsDraft(data.settings || {});
    } catch (err) {
      console.error('Error cargando settings de estancamiento', err);
    }
  };

  const saveStageSettings = async () => {
    setSavingSettings(true);
    try {
      const res = await apiFetch('/api/clients/lead-stages/settings', {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ settings: settingsDraft })
      });
      const data = await parseJsonSafe(res);
      if (res.status === 401) return handleUnauthorized();
      if (!res.ok) throw new Error(data.message || 'No se pudo guardar');
      setStageSettings(data.settings || {});
      setSettingsDraft(data.settings || {});
      setSettingsOpen(false);
    } catch (err) {
      alert(err.message);
    } finally {
      setSavingSettings(false);
    }
  };

  useEffect(() => {
    fetchLeads();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search, stageFilter]);

  useEffect(() => {
    loadStageSettings();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!showClosed && closedStages.has(stageFilter)) {
      setStageFilter('');
    }
  }, [showClosed, stageFilter]);

  const grouped = useMemo(() => {
    const now = Date.now();
    const thresholds = stageSettings || {};
    const base = stageOptions.reduce((acc, stage) => {
      acc[stage.value] = [];
      return acc;
    }, {});
    const filtered = leads.filter((lead) => {
      if (!showStaleOnly) return true;
      const stage = normalizeStage(lead.lead_stage);
      const threshold = Number(thresholds[stage] ?? 0);
      if (!threshold || threshold <= 0) return false;
      const changedAt = lead.lead_stage_changed_at || lead.updated_at || lead.created_at;
      if (!changedAt) return false;
      const days = Math.floor((now - new Date(changedAt).getTime()) / (1000 * 60 * 60 * 24));
      return days >= threshold;
    });
    filtered.forEach((lead) => {
      const stage = normalizeStage(lead.lead_stage);
      base[stage].push({ ...lead, lead_stage: stage });
    });
    return base;
  }, [leads, showStaleOnly, stageSettings]);

  const totals = useMemo(() => {
    const total = leads.length;
    const closed = leads.filter((lead) => closedStages.has(normalizeStage(lead.lead_stage))).length;
    return { total, closed, active: total - closed };
  }, [leads]);

  // Suma de presupuestos abiertos (Enviado/Pendiente) de los leads que SE VEN en el
  // tablero: solo las columnas visibles (respeta "mostrar ganados/perdidos") y el
  // filtro de estancados (grouped ya está filtrado).
  const openBudgetsTotal = useMemo(
    () => visibleStages.reduce(
      (sum, stage) => sum + (grouped[stage.value] || []).reduce((s, lead) => s + Number(lead.budgets_open_total || 0), 0),
      0
    ),
    [visibleStages, grouped]
  );

  const getStaleInfo = (lead) => {
    const stage = normalizeStage(lead.lead_stage);
    const threshold = Number(stageSettings?.[stage] ?? 0);
    if (!threshold || threshold <= 0) return { isStale: false, days: 0, threshold };
    const changedAt = lead.lead_stage_changed_at || lead.updated_at || lead.created_at;
    if (!changedAt) return { isStale: false, days: 0, threshold };
    const days = Math.floor((Date.now() - new Date(changedAt).getTime()) / (1000 * 60 * 60 * 24));
    return { isStale: days >= threshold, days, threshold };
  };

  const updateLeadStage = async (id, stage) => {
    const nextStage = normalizeStage(stage);
    const lead = leads.find((l) => l.id === id);
    try {
      const res = await apiFetch(`/api/clients/${id}/lead-stage`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ leadStage: nextStage })
      });
      const data = await parseJsonSafe(res);
      if (res.status === 401) return handleUnauthorized();
      if (!res.ok) throw new Error(data.message || 'No se pudo actualizar la etapa');
      setLeads((prev) => prev.map((l) => (l.id === id ? { ...l, lead_stage: nextStage, lead_stage_changed_at: data.lead_stage_changed_at || new Date().toISOString(), tipo: data.tipo || l.tipo, estado: data.estado || l.estado } : l)));
      // Al GANAR: el backend ya lo ha convertido en Cliente. Ofrecer crear el proyecto
      // del presupuesto aceptado; si acepta, se abre el formulario completo.
      if (nextStage === 'ganado') {
        if (window.confirm('Este lead ha aceptado el presupuesto y pasa a Cliente.\n\n¿Quieres crear ahora un proyecto para este cliente?')) {
          setProjectModal({ open: true, clientId: id, clientName: lead?.razon_social || '' });
        }
      }
    } catch (err) {
      alert(err.message);
    }
  };

  const handleDragStart = (event, id) => {
    event.dataTransfer.setData('text/plain', String(id));
    event.dataTransfer.effectAllowed = 'move';
  };

  const handleDrop = (event, stage) => {
    event.preventDefault();
    const rawId = event.dataTransfer.getData('text/plain');
    const id = Number(rawId);
    if (!Number.isFinite(id)) return;
    const lead = leads.find((l) => l.id === id);
    if (!lead) return;
    const currentStage = normalizeStage(lead.lead_stage);
    if (currentStage === stage) return;
    updateLeadStage(id, stage);
  };

  const handleDragOver = (event) => {
    event.preventDefault();
    event.dataTransfer.dropEffect = 'move';
  };

  const openCommsModal = (lead) => {
    setCommsModal({ open: true, lead });
    resetCommForm();
    setCommsList([]);
    setCommsExpanded({});
    setCommsError('');
    fetchLeadComms(lead.id);
  };

  const resetCommForm = () => {
    setCommsEditing(null);
    setCommsForm(createCommForm());
  };

  const editLeadComm = (communication) => {
    if (!communication.is_manual || commsSaving) return;
    setCommsEditing(communication);
    setCommsForm({
      canal: communication.canal,
      direccion: communication.direccion,
      asunto: communication.asunto || '',
      cuerpo_texto: communication.cuerpo_texto || '',
      fecha: toLocalDateTimeString(new Date(communication.fecha))
    });
    setCommsError('');
    commsContentRef.current?.focus();
  };

  const fetchLeadComms = async (leadId) => {
    if (!leadId) return;
    setCommsLoading(true);
    setCommsError('');
    try {
      const res = await apiFetch(`/api/clients/${leadId}/communications?page=1&pageSize=50`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const data = await parseJsonSafe(res);
      if (res.status === 401) return handleUnauthorized();
      if (!res.ok) throw new Error(data.message || 'Error cargando comunicaciones');
      setCommsList(Array.isArray(data.items) ? data.items : Array.isArray(data) ? data : []);
    } catch (err) {
      setCommsError(err.message);
      setCommsList([]);
    } finally {
      setCommsLoading(false);
    }
  };

  const saveLeadComm = async () => {
    if (!commsModal.lead?.id || commsSaving) return;
    if (!commsForm.cuerpo_texto.trim()) return alert('El contenido es obligatorio');
    if (commsEditing && !commsForm.fecha) return alert('La fecha es obligatoria');
    setCommsSaving(true);
    try {
      const url = `/api/clients/${commsModal.lead.id}/communications${commsEditing ? `/${commsEditing.id}` : ''}`;
      const dateUnchanged = commsEditing && commsForm.fecha === toLocalDateTimeString(new Date(commsEditing.fecha));
      const res = await apiFetch(url, {
        method: commsEditing ? 'PUT' : 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({
          canal: commsForm.canal,
          direccion: commsForm.direccion,
          asunto: commsForm.asunto,
          cuerpo_texto: commsForm.cuerpo_texto,
          fecha: dateUnchanged ? commsEditing.fecha : commsForm.fecha ? new Date(commsForm.fecha).toISOString() : ''
        })
      });
      const data = await parseJsonSafe(res);
      if (res.status === 401) return handleUnauthorized();
      if (!res.ok) throw new Error(data.message || 'No se pudo guardar la comunicación');
      if (commsEditing) resetCommForm();
      else setCommsForm((prev) => ({ ...prev, cuerpo_texto: '', asunto: '' }));
      await fetchLeadComms(commsModal.lead.id);
    } catch (err) {
      alert(err.message);
    } finally {
      setCommsSaving(false);
    }
  };

  return (
    <div>
      <div className="d-flex justify-content-between align-items-center mb-3 flex-wrap gap-2">
        <div className="d-flex align-items-center gap-2 flex-wrap">
          <h2 className="mb-0">Clientes/Leads</h2>
          <div className="btn-group">
            <Link className="btn btn-sm btn-outline-primary" to="/clientes">Tabla Clientes</Link>
            <Link className="btn btn-sm btn-primary" to="/clientes/leads">Leads</Link>
          </div>
        </div>
        <div className="d-flex align-items-center gap-2">
          {loading && <span className="badge bg-info text-dark">Cargando...</span>}
          <button className="btn btn-outline-secondary btn-sm" onClick={fetchLeads} disabled={loading}>
            Actualizar
          </button>
          <button className="btn btn-outline-primary btn-sm" onClick={() => { setSettingsOpen(true); }}>
            Configurar estancamiento
          </button>
        </div>
      </div>

      <div className="card shadow-sm mb-3">
        <div className="card-body d-flex flex-wrap gap-2 align-items-center">
          <input
            type="text"
            className="form-control"
            placeholder="Buscar por nombre, email, ciudad o teléfono"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            style={{ maxWidth: 320 }}
          />
          <select
            className="form-select"
            value={stageFilter}
            onChange={(e) => setStageFilter(e.target.value)}
            style={{ width: 200 }}
          >
            <option value="">Todas las etapas</option>
            {(showClosed ? stageOptions : visibleStages).map((stage) => (
              <option key={stage.value} value={stage.value}>{stage.label}</option>
            ))}
          </select>
          <div className="form-check form-switch">
            <input
              className="form-check-input"
              type="checkbox"
              id="showStale"
              checked={showStaleOnly}
              onChange={(e) => setShowStaleOnly(e.target.checked)}
            />
            <label className="form-check-label small" htmlFor="showStale">
              Solo estancados
            </label>
          </div>
          <div className="form-check form-switch ms-auto">
            <input
              className="form-check-input"
              type="checkbox"
              id="showClosed"
              checked={showClosed}
              onChange={(e) => setShowClosed(e.target.checked)}
            />
            <label className="form-check-label small" htmlFor="showClosed">
              Mostrar ganados/perdidos
            </label>
          </div>
          <div className="text-muted small">
            Activos: {totals.active} · Cerrados: {totals.closed} · Total: {totals.total}
          </div>
          {openBudgetsTotal > 0 && (
            <div className="ms-auto">
              <span className="badge bg-primary-subtle text-primary border py-2 px-3" style={{ fontSize: '0.9rem' }} title="Base imponible (sin IVA) de los presupuestos Enviado/Pendiente de los leads visibles en el tablero">
                Presupuestos abiertos (tablero): {formatCurrency(openBudgetsTotal)}
              </span>
            </div>
          )}
        </div>
      </div>

      {error && <div className="alert alert-danger">{error}</div>}

      <div className="d-flex gap-3 overflow-auto pb-2">
        {visibleStages.map((stage) => (
          <div key={stage.value} style={{ minWidth: 280, maxWidth: 320, flex: '0 0 auto' }}>
            <div className="card shadow-sm h-100">
              <div className="card-header d-flex justify-content-between align-items-center">
                <span>{stage.label}</span>
                <span className={`badge bg-${stage.color}`}>{grouped[stage.value]?.length || 0}</span>
              </div>
              <div
                className="card-body"
                style={{ minHeight: 240, background: '#f8fafc' }}
                onDragOver={handleDragOver}
                onDrop={(event) => handleDrop(event, stage.value)}
              >
                {(grouped[stage.value] || []).length ? (
                  grouped[stage.value].map((lead) => (
                    (() => {
                      const staleInfo = getStaleInfo(lead);
                      const staleClass = staleInfo.isStale ? 'border-danger' : '';
                      return (
                    <div
                      key={lead.id}
                      className={`card mb-2 shadow-sm ${staleClass}`}
                      draggable
                      onDragStart={(event) => handleDragStart(event, lead.id)}
                    >
                      <div className="card-body p-2">
                        {/* minWidth:0 + overflowWrap: un email largo (sin espacios) se parte dentro de su
                            columna en vez de ensancharla y empujar/ocultar los botones, que no encogen. */}
                        <div className="d-flex justify-content-between align-items-start gap-2">
                          <div className="flex-grow-1" style={{ minWidth: 0, overflowWrap: 'anywhere' }}>
                            <div className="fw-semibold">{lead.razon_social}</div>
                            <div className="small text-muted">{lead.email || 'Sin email'}</div>
                            <div className="small text-muted">{lead.telefono || 'Sin teléfono'}</div>
                          </div>
                          <div className="d-flex gap-1 flex-shrink-0">
                            <Link className="btn btn-sm btn-outline-primary" title="Crear tarea" to={`/tareas?create=1&clientId=${lead.id}`}>Tarea</Link>
                            <Link className="btn btn-sm btn-outline-secondary" to={`/clientes/${lead.id}`}>Ver</Link>
                            <button
                              className="btn btn-sm btn-outline-primary"
                              type="button"
                              onClick={() => openCommsModal(lead)}
                              title="Comunicaciones"
                              aria-label="Comunicaciones"
                            >
                              <FiEdit2 />
                            </button>
                          </div>
                        </div>
                        <div className="small mt-2">
                          {lead.ciudad || 'Sin ciudad'}{lead.provincia ? ` · ${lead.provincia}` : ''}
                        </div>
                        <div className="d-flex flex-wrap gap-1 mt-2">
                          <span className="badge bg-light text-dark border">{lead.canal_captacion || 'Sin establecer'}</span>
                          {lead.canal_captacion === 'Webhook' && (
                            <>
                              {lead.utm_medium && <span className="badge bg-light text-dark border">UTM {lead.utm_medium}</span>}
                              {lead.utm_campaign && <span className="badge bg-light text-dark border">{lead.utm_campaign}</span>}
                            </>
                          )}
                        </div>
                        {Number(lead.budgets_open_total) > 0 && (
                          <div className="mt-2">
                            <span
                              className="badge bg-primary-subtle text-primary border w-100 py-2"
                              style={{ fontSize: '0.85rem' }}
                              title="Base imponible (sin IVA) de los presupuestos enviados o pendientes"
                            >
                              Presupuestos abiertos: {formatCurrency(lead.budgets_open_total)}
                            </span>
                          </div>
                        )}
                        {staleInfo.isStale && (
                          <div className="mt-2">
                            <span className="badge bg-danger">Estancado {staleInfo.days} días</span>
                          </div>
                        )}
                        <div className="mt-2">
                          <select
                            className="form-select form-select-sm"
                            value={normalizeStage(lead.lead_stage)}
                            onChange={(e) => updateLeadStage(lead.id, e.target.value)}
                          >
                            {stageOptions.map((option) => (
                              <option key={option.value} value={option.value}>{option.label}</option>
                            ))}
                          </select>
                        </div>
                      </div>
                    </div>
                      );
                    })()
                  ))
                ) : (
                  <div className="text-muted small">Sin leads</div>
                )}
              </div>
            </div>
          </div>
        ))}
      </div>

      {settingsOpen && (
        <div className="modal fade show" style={{ display: 'block', background: 'rgba(0,0,0,0.4)' }}>
          <div className="modal-dialog">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">Estancamiento por etapa</h5>
                <button className="btn-close" onClick={() => setSettingsOpen(false)}></button>
              </div>
              <div className="modal-body">
                <p className="text-muted small mb-3">Define el número de días sin avance para marcar un lead como estancado. 0 desactiva la alerta.</p>
                <div className="d-flex flex-column gap-2">
                  {stageOptions.map((stage) => (
                    <div key={stage.value} className="d-flex align-items-center justify-content-between gap-3">
                      <div className="fw-semibold">{stage.label}</div>
                      <input
                        type="number"
                        min="0"
                        className="form-control form-control-sm"
                        style={{ width: 110 }}
                        value={Number(settingsDraft?.[stage.value] ?? 0)}
                        onChange={(e) => {
                          const value = Math.max(0, Number(e.target.value || 0));
                          setSettingsDraft((prev) => ({ ...prev, [stage.value]: value }));
                        }}
                      />
                    </div>
                  ))}
                </div>
              </div>
              <div className="modal-footer">
                <button className="btn btn-outline-secondary" onClick={() => setSettingsOpen(false)}>Cancelar</button>
                <button className="btn btn-primary" onClick={saveStageSettings} disabled={savingSettings}>
                  {savingSettings ? 'Guardando...' : 'Guardar'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      <ProjectCreateModal
        open={projectModal.open}
        clientId={projectModal.clientId}
        clientName={projectModal.clientName}
        onClose={() => { setProjectModal({ open: false, clientId: null, clientName: '' }); fetchLeads(); }}
        onCreated={() => { setProjectModal({ open: false, clientId: null, clientName: '' }); fetchLeads(); }}
      />

      {commsModal.open && (
        <div className="modal fade show" style={{ display: 'block', background: 'rgba(0,0,0,0.4)' }}>
          <div className="modal-dialog modal-lg modal-dialog-scrollable">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">Comunicaciones · {commsModal.lead?.razon_social || `Lead #${commsModal.lead?.id}`}</h5>
                <button className="btn-close" disabled={commsSaving} onClick={() => setCommsModal({ open: false, lead: null })}></button>
              </div>
              <div className="modal-body">
                <div className="card mb-3">
                  <fieldset className="card-body" disabled={commsSaving}>
                    <h6 className="mb-2">{commsEditing ? 'Editar comunicación' : 'Añadir comunicación'}</h6>
                    <div className="row g-2 mb-2">
                      <div className="col-md-3">
                        <label className="form-label small">Canal</label>
                        <select
                          className="form-select"
                          value={commsForm.canal}
                          onChange={(e) => setCommsForm((p) => ({ ...p, canal: e.target.value }))}
                        >
                          <option value="call">Llamada</option>
                          <option value="whatsapp">WhatsApp</option>
                          <option value="email">Email</option>
                          <option value="note">Nota</option>
                        </select>
                      </div>
                      <div className="col-md-3">
                        <label className="form-label small">Dirección</label>
                        <select
                          className="form-select"
                          value={commsForm.direccion}
                          onChange={(e) => setCommsForm((p) => ({ ...p, direccion: e.target.value }))}
                        >
                          <option value="out">Saliente</option>
                          <option value="in">Entrante</option>
                        </select>
                      </div>
                      <div className="col-md-3">
                        <label className="form-label small">Fecha</label>
                        <input
                          className="form-control"
                          type="datetime-local"
                          value={commsForm.fecha}
                          onChange={(e) => setCommsForm((p) => ({ ...p, fecha: e.target.value }))}
                        />
                      </div>
                      <div className="col-md-3">
                        <label className="form-label small">Asunto (opcional)</label>
                        <input
                          className="form-control"
                          value={commsForm.asunto}
                          onChange={(e) => setCommsForm((p) => ({ ...p, asunto: e.target.value }))}
                        />
                      </div>
                    </div>
                    <div className="mb-2">
                      <label className="form-label small">Contenido</label>
                      <textarea
                        ref={commsContentRef}
                        className="form-control"
                        rows="3"
                        value={commsForm.cuerpo_texto}
                        onChange={(e) => setCommsForm((p) => ({ ...p, cuerpo_texto: e.target.value }))}
                        placeholder="Resumen de la comunicación"
                      />
                    </div>
                    <div className="d-flex justify-content-end gap-2">
                      {commsEditing && (
                        <button className="btn btn-sm btn-outline-secondary" type="button" onClick={resetCommForm}>
                          Cancelar edición
                        </button>
                      )}
                      <button className="btn btn-sm btn-primary" type="button" onClick={saveLeadComm} disabled={commsSaving}>
                        {commsSaving ? 'Guardando...' : commsEditing ? 'Guardar cambios' : 'Guardar'}
                      </button>
                    </div>
                  </fieldset>
                </div>

                <h6 className="mb-2">Histórico</h6>
                {commsError && <div className="alert alert-danger">{commsError}</div>}
                {commsLoading ? (
                  <p>Cargando...</p>
                ) : commsList.length ? (
                  <div className="table-responsive">
                    <table className="table table-sm align-middle">
                      <thead>
                        <tr>
                          <th>Canal</th>
                          <th>Dirección</th>
                          <th>Asunto / Nota</th>
                          <th>Fecha</th>
                          <th>Acciones</th>
                        </tr>
                      </thead>
                      <tbody>
                        {commsList.map((c) => (
                          <tr key={`${c.is_manual ? 'manual' : c.canal}-${c.id}`}>
                            <td>{c.canal === 'whatsapp' && c.provider ? `WhatsApp ${c.provider === 'qr' ? 'QR' : 'API'}` : c.canal}</td>
                            <td>{c.direccion === 'in' ? 'Entrante' : 'Saliente'}</td>
                            <td>
                              <div className="fw-semibold">{c.asunto || '(sin asunto)'}</div>
                              {(() => {
                                const fullText = c.cuerpo_texto || '';
                                const isLong = fullText.length > 180;
                                const key = `${c.is_manual ? 'manual' : c.canal}-${c.id}`;
                                const isExpanded = commsExpanded[key];
                                const text = isLong && !isExpanded ? `${fullText.slice(0, 180)}…` : fullText;
                                return (
                                  <>
                                    <div className="text-muted small" style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                                      {text}
                                    </div>
                                    {isLong && (
                                      <button
                                        type="button"
                                        className="btn btn-link p-0 small"
                                        onClick={() => setCommsExpanded((prev) => ({ ...prev, [key]: !prev[key] }))}
                                      >
                                        {isExpanded ? 'Ver menos' : 'Ver más'}
                                      </button>
                                    )}
                                  </>
                                );
                              })()}
                            </td>
                            <td className="small">{new Date(c.fecha).toLocaleString()}</td>
                            <td>
                              {c.is_manual && (
                                <button
                                  className="btn btn-sm btn-outline-primary"
                                  type="button"
                                  disabled={commsSaving}
                                  onClick={() => editLeadComm(c)}
                                >
                                  <FiEdit2 className="me-1" />Editar
                                </button>
                              )}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                ) : (
                  <p className="text-muted">Sin comunicaciones.</p>
                )}
              </div>
              <div className="modal-footer">
                <button className="btn btn-outline-secondary" disabled={commsSaving} onClick={() => setCommsModal({ open: false, lead: null })}>
                  Cerrar
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ClientsLeadsPage;
